/**
 * Pairing crypto. The pairing code is the only shared secret:
 *  - pairId  = SHA-256("ffp-pair-id:" + code)   → the relay's identifier
 *  - AES key = HKDF(code, salt "ffp-pair-key")  → encrypts every payload
 *
 * The relay only ever sees pairId and ciphertext, so it cannot decrypt
 * anything and cannot reverse the code from the id.
 *
 * Both derivations are fast, unsalted-per-pair hashes, so the code itself
 * carries the entire brute-force margin: an attacker holding the relay's
 * stored pairIds can hash candidate codes offline and test them against
 * every pair at once. 16 Crockford chars = 80 bits keeps that infeasible
 * (a 10-char/50-bit code would fall to a single GPU in about a day).
 * Codes normally travel by QR or link, so the extra length costs little.
 */

// Crockford base32: no I, L, O, U — unambiguous to read aloud or type.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 16;

export function generatePairCode(): string {
  const bytes = new Uint8Array(CODE_LENGTH);
  crypto.getRandomValues(bytes);
  let code = '';
  for (const b of bytes) code += ALPHABET[b % 32];
  return code;
}

/** Uppercases and strips separators/ambiguous characters from user input. */
export function normalizeCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    // The generator never emits U (Crockford treats it as invalid); a typed
    // U is almost always a misread V, so map it instead of accepting a code
    // that can never match a real pair.
    .replace(/U/g, 'V');
}

/** Groups a code into dash-separated blocks of 4 for display. */
export function formatCode(code: string): string {
  return code.replace(/(.{4})(?=.)/g, '$1-');
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export async function derivePairId(code: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(`ffp-pair-id:${code}`));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function deriveKey(code: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', enc.encode(code), 'HKDF', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('ffp-pair-key'), info: enc.encode('aes') },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(b64: string): Uint8Array {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
}

/** Encrypts a JSON-serializable value to base64(iv || ciphertext). */
export async function encryptJson(key: CryptoKey, value: unknown): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(JSON.stringify(value)),
  );
  const out = new Uint8Array(iv.length + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), iv.length);
  return toB64(out);
}

export async function decryptJson<T>(key: CryptoKey, blob: string): Promise<T> {
  const bytes = fromB64(blob);
  const iv = bytes.slice(0, 12);
  const ct = bytes.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(dec.decode(pt)) as T;
}
